import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  photoUrl,
  tournamentApi,
  uploadEntryPhoto,
  type PlayerEntry,
} from '@/api/tournament';
import { CasinoButton } from '@/components/CasinoButton';
import { LedNumber } from '@/components/LedNumber';
import { cn } from '@/lib/utils';

type Which = 'start' | 'end';

export function SubmitScore() {
  const { entryId: entryIdStr } = useParams<{ entryId: string }>();
  const entryId = Number(entryIdStr);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['tournament', 'active'],
    queryFn: tournamentApi.active,
    refetchOnMount: 'always',
  });

  const submit = useMutation({
    mutationFn: () => tournamentApi.submit(entryId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['tournament', 'active'] });
      navigate('/tournament/active');
    },
  });

  const myEntry = q.data?.myEntries.find((e) => e.id === entryId);

  if (!Number.isInteger(entryId) || entryId <= 0) {
    return (
      <main className="mx-auto max-w-md px-5 py-12 text-center">
        <p className="text-cabinet-flame-red">Invalid entry.</p>
      </main>
    );
  }

  if (q.isLoading) {
    return <Loading />;
  }
  if (!myEntry) {
    return (
      <main className="mx-auto max-w-md px-5 py-12 text-center space-y-3">
        <p className="text-cabinet-flame-red">Entry not found.</p>
        <Link to="/tournament/active" className="text-cabinet-gold underline-offset-2 hover:underline">
          ← Back to dashboard
        </Link>
      </main>
    );
  }
  if (myEntry.submittedAt) {
    return (
      <main className="mx-auto max-w-md px-5 py-12 text-center space-y-3">
        <p className="font-display text-cabinet-gold text-xl uppercase tracking-widest">
          Already submitted
        </p>
        <p className="text-cabinet-cream/70 text-sm">
          Entry #{myEntry.entryNumber} is awaiting verification.
        </p>
        <Link to="/tournament/active" className="text-cabinet-gold underline-offset-2 hover:underline">
          ← Back to dashboard
        </Link>
      </main>
    );
  }

  const startReady = myEntry.startingBalance !== null && myEntry.hasStartingPhoto;
  const endReady = myEntry.endingBalance !== null && myEntry.hasEndingPhoto;
  const score =
    myEntry.startingBalance !== null && myEntry.endingBalance !== null
      ? myEntry.endingBalance - myEntry.startingBalance
      : null;

  return (
    <main className="mx-auto max-w-md px-5 py-6 space-y-5">
      <header className="space-y-2">
        <Link
          to="/tournament/active"
          className="text-sm text-cabinet-cream/60 hover:text-cabinet-gold"
        >
          ← Back to tournament
        </Link>
        <h1 className="font-display text-cabinet-gold text-2xl uppercase tracking-widest">
          Submit entry #{myEntry.entryNumber}
        </h1>
      </header>

      <PhotoStep
        entryId={entryId}
        which="start"
        title="Starting balance"
        instructions="Snap the credit display BEFORE you play. Enter the number you see."
        entry={myEntry}
      />

      <PhotoStep
        entryId={entryId}
        which="end"
        title="Ending balance"
        instructions="After your pulls, snap the final credit display. Enter the number you see."
        entry={myEntry}
        disabled={!startReady}
      />

      <ScorePreview score={score} />

      <CasinoButton
        fullWidth
        variant="red"
        disabled={!startReady || !endReady || submit.isPending}
        onClick={() => submit.mutate()}
      >
        {submit.isPending ? 'Submitting…' : 'Submit final score'}
      </CasinoButton>

      {submit.isError && (
        <div className="rounded-md bg-cabinet-flame-red/20 border border-cabinet-flame-red px-3 py-2 text-sm text-cabinet-cream">
          Couldn't submit. Make sure both balances are entered and photos uploaded.
        </div>
      )}
    </main>
  );
}

function PhotoStep({
  entryId,
  which,
  title,
  instructions,
  entry,
  disabled,
}: {
  entryId: number;
  which: Which;
  title: string;
  instructions: string;
  entry: PlayerEntry;
  disabled?: boolean;
}) {
  const qc = useQueryClient();
  const initialBalance =
    which === 'start' ? entry.startingBalance : entry.endingBalance;
  const hasPhoto = which === 'start' ? entry.hasStartingPhoto : entry.hasEndingPhoto;

  const [balance, setBalance] = useState<string>(
    initialBalance !== null ? String(initialBalance) : '',
  );
  const [pickedFile, setPickedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadPct, setUploadPct] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Local preview URL lifecycle.
  useEffect(() => {
    if (!pickedFile) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(pickedFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pickedFile]);

  // Keep balance synced with server-confirmed value once uploaded.
  useEffect(() => {
    if (initialBalance !== null && balance === '') {
      setBalance(String(initialBalance));
    }
  }, [initialBalance, balance]);

  async function handleUpload() {
    if (disabled || !pickedFile || uploading) return;
    const bal = Number(balance);
    if (!Number.isInteger(bal) || bal < 0) {
      setError('Enter the whole-number balance from the credit display.');
      return;
    }
    setError(null);
    setUploading(true);
    setUploadPct(0);
    try {
      await uploadEntryPhoto({
        entryId,
        which,
        balance: bal,
        file: pickedFile,
        onProgress: (loaded, total) =>
          setUploadPct(total > 0 ? Math.round((loaded / total) * 100) : 0),
      });
      await qc.invalidateQueries({ queryKey: ['tournament', 'active'] });
      setPickedFile(null);
    } catch (err) {
      const e = err as { error?: string; message?: string };
      setError(
        e.message ??
          (e.error === 'too_large'
            ? 'Photo too large — try a smaller one.'
            : e.error === 'unsupported' || e.error === 'decode_failed'
              ? "Couldn't read that image. Try a different photo."
              : 'Upload failed. Try again.'),
      );
    } finally {
      setUploading(false);
    }
  }

  const inputCls =
    'w-full rounded-md bg-black/50 border-2 border-cabinet-cream/20 px-3 py-3 text-cabinet-cream focus:border-cabinet-gold focus:outline-none focus:ring-2 focus:ring-cabinet-gold/40 transition tabular-nums text-lg';

  return (
    <section
      className={cn(
        'cabinet-card p-5 space-y-3 transition-opacity',
        disabled && 'opacity-50 pointer-events-none',
      )}
    >
      <header className="flex items-center justify-between gap-3">
        <h2 className="font-display text-cabinet-gold text-lg uppercase tracking-widest">
          {title}
        </h2>
        {hasPhoto && (
          <span className="rounded-full bg-cabinet-gold/20 ring-1 ring-cabinet-gold/40 px-2 py-0.5 text-[10px] uppercase tracking-widest text-cabinet-gold-bright">
            Saved ✓
          </span>
        )}
      </header>
      <p className="text-sm text-cabinet-cream/70 leading-snug">{instructions}</p>

      <label className="block">
        <span className="block text-xs uppercase tracking-widest text-cabinet-cream/60 mb-1">
          Balance (whole number)
        </span>
        <input
          type="number"
          inputMode="numeric"
          step="1"
          min={0}
          value={balance}
          onChange={(e) => setBalance(e.target.value.replace(/\D/g, ''))}
          placeholder="0"
          className={inputCls}
        />
      </label>

      <div className="space-y-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) setPickedFile(f);
          }}
        />

        {previewUrl ? (
          <div className="space-y-2">
            <img
              src={previewUrl}
              alt="Local preview"
              className="block max-h-64 w-full object-contain rounded-md ring-1 ring-cabinet-cream/20 bg-black/50"
            />
            <div className="flex items-center gap-2">
              <CasinoButton
                type="button"
                onClick={handleUpload}
                disabled={uploading || !balance}
              >
                {uploading ? `Uploading ${uploadPct}%` : 'Upload photo'}
              </CasinoButton>
              <button
                type="button"
                onClick={() => {
                  setPickedFile(null);
                  if (inputRef.current) inputRef.current.value = '';
                }}
                className="text-sm text-cabinet-cream/60 hover:text-cabinet-cream px-3 min-h-[44px]"
              >
                Pick again
              </button>
            </div>
            {uploading && (
              <div
                className="h-1.5 rounded-full bg-cabinet-cream/10 overflow-hidden"
                aria-label="Upload progress"
              >
                <div
                  className="h-full bg-cabinet-gold"
                  style={{ width: `${uploadPct}%`, transition: 'width 200ms' }}
                />
              </div>
            )}
          </div>
        ) : hasPhoto ? (
          <div className="space-y-2">
            <img
              src={photoUrl(entryId, which)}
              alt={`${title} thumbnail`}
              className="block max-h-64 w-full object-contain rounded-md ring-1 ring-cabinet-gold/30 bg-black/50"
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="text-sm text-cabinet-cream/70 hover:text-cabinet-gold w-full text-center py-3 min-h-[44px] rounded-md ring-1 ring-cabinet-cream/20"
            >
              Re-take photo
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="btn-casino btn-casino-ghost w-full"
          >
            📷 Take photo
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-md bg-cabinet-flame-red/20 border border-cabinet-flame-red px-3 py-2 text-sm text-cabinet-cream">
          {error}
        </div>
      )}
    </section>
  );
}

function ScorePreview({ score }: { score: number | null }) {
  return (
    <section className="cabinet-card p-5">
      <div className="chrome-bezel">
        <div className="rounded-[0.625rem] bg-black p-4 flex flex-col items-center gap-2">
          <p className="text-[10px] uppercase tracking-widest text-cabinet-cream/60">
            Your score
          </p>
          {score === null ? (
            <p className="font-led text-cabinet-led-dim text-4xl">— —</p>
          ) : (
            <LedNumber value={score} digits={5} size="xl" />
          )}
          <p className="text-[10px] uppercase tracking-widest text-cabinet-cream/40">
            Ending − Starting
          </p>
        </div>
      </div>
    </section>
  );
}

function Loading() {
  return (
    <main className="mx-auto flex min-h-dvh max-w-md items-center justify-center px-5">
      <p className="text-cabinet-cream/60">Loading…</p>
    </main>
  );
}
